import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Typography, Row, Col, Grid, Input, Flex, message, Spin, Modal } from 'antd';
import {
  PlayCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import liveStreamBackground from '../assets/live-stream-background.png';
import chatroomBackground from '../assets/chartroom-background.png';
import { ReactComponent as ShareIcon } from '../assets/share-icon.svg';
import { ReactComponent as SendIcon } from '../assets/send-icon.svg';
import flowerImage from '../assets/flower.png';
import VideoSection from '../components/VideoSection';
import { useDemand } from '../context/DemandContext';
import { useDemandDataLoader } from '../hooks/demand/useDemandDataLoader';
import { useLivestreamChat } from '../hooks/useLivestreamChat';
import { CHAT_MESSAGE_TYPES } from '../config/constants';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface ChatMessage {
  id: number;
  user: string;
  message: string;
  time: string;
  type: keyof typeof CHAT_MESSAGE_TYPES; // 只支持 API 定義的格式
}

interface VideoInfo {
  title: string;
  viewCount: string;
  publishedAt: string;
  channelTitle: string;
  description: string;
  likeCount?: string;
  isLive?: boolean;
  duration?: string;
}

const LiveStream: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [isLive] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [selectedQuickAction, setSelectedQuickAction] = useState<string>('');
  const [isJoinedChat, setIsJoinedChat] = useState(false); // 是否已加入聊天室
  const [showJoinModal, setShowJoinModal] = useState(false); // 是否顯示加入彈窗
  const [inputName, setInputName] = useState(''); // 用戶輸入的名稱
  const isDesktop = screens.lg; // lg 斷點及以上為桌面版
  const isMobile = !screens.lg; // lg 斷點以下為移動版
  const hasLoadedRef = useRef(false); // 追蹤是否已經載入過

  // 從 DemandContext 取得直播設定
  const { state } = useDemand();
  const { isLoading: isItemLoading, error, loadCurrentMemberData } = useDemandDataLoader();

  // 數據載入邏輯
  useEffect(() => {
    const loadData = async () => {
      // 如果已經載入過，就不再載入
      if (hasLoadedRef.current) {
        return;
      }

      // 從 localStorage 獲取會員資料
      const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');

      if (memberInfo.projectName) {
        hasLoadedRef.current = true; // 標記為已載入
        const success = await loadCurrentMemberData();
        if (!success) {
          console.error('載入需求單數據失敗');
          hasLoadedRef.current = false; // 載入失敗時重置標記
        } else {
          console.log('需求單數據載入成功');
          // 聊天室初始化將在房間查詢完成後進行
        }
      } else {
        message.error('未找到會員資訊');
        navigate('/');
      }
    };

    loadData();
  }, []); // 空依賴項，只在組件掛載時執行

  // 從 localStorage 獲取會員資料（用於顯示）
  const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');

  const liveUrl = state.live.url;
  const isLiveEnabled = state.live.isEnabled;
  const roomCode = state.live.roomCode; // 從 DemandContext 讀取房間代碼

  // 穩定 useLivestreamChat 的 options 對象，避免重新渲染導致 MQTT 斷線
  const chatOptions = useMemo(() => ({
    autoCreateRoom: false,
    enableQuery: true
  }), []);

  // 聊天室功能 - 只有當房間代碼存在時才啟用，不自動創建房間
  const {
    messages: chatMessages,
    currentViewer,
    isConnected,
    isLoading: isChatLoading,
    initializeChat,
    sendMessage,
    sendQuickAction,
    isSending
  } = useLivestreamChat(roomCode || '', chatOptions);

  // 調試日誌
  useEffect(() => {
    console.log('LiveStream - chatMessages 更新:', chatMessages);
    console.log('LiveStream - chatMessages 長度:', chatMessages?.length || 0);
    console.log('LiveStream - isConnected:', isConnected);
    console.log('LiveStream - isJoinedChat:', isJoinedChat);
  }, [chatMessages, isConnected, isJoinedChat]);

  // 初始化用戶名稱
  useEffect(() => {
    const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');
    const defaultName = memberInfo.name || '訪客';
    setInputName(defaultName);
  }, []);

  // 處理加入聊天室
  const handleJoinChat = () => {
    if (!roomCode) {
      message.error('聊天室尚未設定');
      return;
    }
    setShowJoinModal(true);
  };

  // 確認加入聊天室
  const handleConfirmJoin = async () => {
    if (!inputName.trim()) {
      message.error('請輸入您的名稱');
      return;
    }

    try {
      const displayName = inputName.trim();

      console.log('開始加入聊天室...');
      console.log('displayName:', displayName);

      await initializeChat(displayName);

      setIsJoinedChat(true);
      setShowJoinModal(false);
      message.success('成功加入聊天室！');
      console.log('聊天室加入成功');


    } catch (error) {
      console.error('加入聊天室失敗:', error);
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      message.error(`加入聊天室失敗: ${errorMessage}`);
    }
  };

  // 取消加入聊天室
  const handleCancelJoin = () => {
    setShowJoinModal(false);
  };

  // YouTube 相關狀態
  const [videoId, setVideoId] = useState<string>('');
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [videoInfo, setVideoInfo] = useState<VideoInfo>({
    title: '載入中...',
    viewCount: '0',
    publishedAt: '',
    channelTitle: '',
    description: '',
    isLive: false
  });
  const [loading, setLoading] = useState(true);

  // 移除靜態聊天訊息，改用動態聊天室數據

  // 提取YouTube視頻ID
  const extractYouTubeId = (url: string): string => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  // 獲取YouTube視頻資訊
  const fetchVideoInfo = async (videoId: string) => {
    try {
      setLoading(true);

      // 使用 YouTube oEmbed API (不需要 API key)
      const oEmbedResponse = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );

      if (!oEmbedResponse.ok) {
        throw new Error('Failed to fetch video info');
      }

      const oEmbedData = await oEmbedResponse.json();

      // 設置基本資訊
      setVideoInfo({
        title: oEmbedData.title || '無法載入標題',
        viewCount: '無法獲取', // oEmbed API 不提供觀看次數
        publishedAt: '',
        channelTitle: oEmbedData.author_name || '未知頻道',
        description: '',
        isLive: false // 無法從 oEmbed 判斷是否直播
      });

    } catch (error) {
      console.error('Error fetching video info:', error);
      setVideoInfo({
        title: '無法載入視頻資訊',
        viewCount: '0',
        publishedAt: '',
        channelTitle: '未知頻道',
        description: '',
        isLive: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = extractYouTubeId(liveUrl);
    setVideoId(id);
    if (id && isLiveEnabled) {
      setIsPlayerReady(true);
      fetchVideoInfo(id);
    } else {
      setIsPlayerReady(false);
    }
  }, [liveUrl, isLiveEnabled]);



  const getMessageStyle = (type: string) => {
    switch (type) {
      case CHAT_MESSAGE_TYPES.LIKE:
        return {
          background: '#FFEFCD',
          userColor: '#D7862A',
          textColor: '#D7862A'
        };
      case CHAT_MESSAGE_TYPES.FOLLOW:
        return {
          background: '#FFD1D1',
          userColor: '#FF7171',
          textColor: '#FF7171'
        };
      case CHAT_MESSAGE_TYPES.SHARE:
        return {
          background: '#AFD2EC',
          userColor: '#2985C9',
          textColor: '#2985C9'
        };
      case CHAT_MESSAGE_TYPES.MESSAGE:
      case CHAT_MESSAGE_TYPES.JOIN:
      case CHAT_MESSAGE_TYPES.LEAVE:
      default:
        return {
          background: 'rgba(255, 255, 255, 0.7)',
          userColor: '#717D8B',
          textColor: '#3E3D3D'
        };
    }
  };

  // 分享處理函數
  const handleShare = async () => {
    const friendName = state.basic.name || '親友';
    const shareData = {
      title: `${friendName}的線上告別式`,
      text: `參與 ${friendName} 的線上告別式，在這裡獻上您的思念與祝福`,
      url: window.location.href
    };

    try {
      // 檢查是否支援 Web Share API
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        message.success('分享成功！');
      } else {
        // 回退到複製連結
        await navigator.clipboard.writeText(window.location.href);
        message.success('連結已複製到剪貼簿！');
      }
    } catch (error) {
      console.error('分享失敗:', error);
      // 如果上述方法都失敗，嘗試直接複製連結
      try {
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('連結已複製到剪貼簿！');
      } catch (fallbackError) {
        message.error('分享失敗，請手動複製連結');
      }
    }
  };

  return (
    <Spin spinning={isItemLoading || (!isConnected && isJoinedChat && isChatLoading)} tip={isItemLoading ? "載入需求單數據中..." : "初始化聊天室中..."}>
      <div style={{
        background: 'linear-gradient(180deg, #F4F1EC 0%, #EDE8DD 100%)',
        position: 'relative',
      }}>
        <div className='relative md:h-[100vh] flex flex-col z-1'>
          {/* 頂部導航 */}
          <Flex
            justify='space-between'
            align='center'
            className='h-[90px] md:h-[20%] md:pr-[100px] px-4'
            style={{
              background: `url(${liveStreamBackground}) center/cover no-repeat`,
            }}
          >
            <Title
              level={3}
              style={{
                flex: 1,
                margin: 0,
                fontSize: isMobile ? 24 : 32,
                fontWeight: 800,
                color: '#222834',
                fontFamily: 'Noto Serif TC',
                textAlign: 'center',
              }}
            >
              {state.basic.name ? `${state.basic.name}的線上告別式` : '線上告別式'}
            </Title>

            <Button
              // type='text'
              className='h-fit rounded-full shadow-md'
              onClick={handleShare}
              style={{
                padding: isMobile ? '0 12px' : '0 16px',
                fontSize: isMobile ? 16 : 36,
                fontWeight: 700,
                fontFamily: 'Noto Serif TC',
                letterSpacing: '5%',
                color: '#3E3D3D',
                background: '#FFFFFF80',
              }}
            >
              <span>分享</span>
              <ShareIcon style={{ fontSize: isMobile ? 16 : 24 }} />
            </Button>
          </Flex>

          <Row gutter={[0, 24]} className='h-[90%] md:h-[80%] shadow-sm'>
            {/* 左側 - YouTube 播放器 */}
            <Col xs={24} lg={16} className='h-[60vh] md:h-full'>
              <div className='h-full flex flex-col bg-white overflow-hidden'>
                {/* YouTube 播放器區域 */}
                <div className='flex-1 w-full bg-black relative flex items-center justify-center rounded-t-lg'>
                  {!isLiveEnabled ? (
                    <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center'>
                      <PlayCircleOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                      <div style={{ fontSize: 20 }}>直播功能未啟用</div>
                      <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>請聯繫管理員開啟直播功能</div>
                    </div>
                  ) : !liveUrl ? (
                    <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center'>
                      <PlayCircleOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                      <div style={{ fontSize: 20 }}>暫無直播連結</div>
                      <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>直播連結尚未設定</div>
                    </div>
                  ) : isPlayerReady && videoId ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0&showinfo=0&fs=1&cc_load_policy=0&iv_load_policy=3&modestbranding=1`}
                      title="視頻播放"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      style={{
                        border: 'none'
                      }}
                    />
                  ) : (
                    <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center'>
                      <PlayCircleOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                      <div style={{ fontSize: 20 }}>載入中...</div>
                    </div>
                  )}
                </div>

                {/* 視頻資訊區 - 顯示真實的 YouTube 資訊 */}
                <div className='p-6'>
                  <Title level={4} style={{
                    fontSize: 18,
                    margin: '0 0 8px 0',
                    color: '#161722'
                  }}>
                    {isLiveEnabled ? loading ? '載入中...' : videoInfo.title : ''}
                  </Title>
                </div>
              </div>
            </Col>

            {/* 右側 - 聊天室 */}
            <Col xs={24} lg={8} className='h-[700px] md:h-full'>
              <div className='h-full flex flex-col al bg-white overflow-hidden'>
                {/* 聊天室標題 */}
                <div className='h-[10%] flex justify-center items-center relative bg-[#EDE8DD]'>
                  <h2 className='text-[#11386F] font-noto-serif text-2xl font-bold'>
                    在聊天室傳出你的思念，讓愛永續
                  </h2>
                  <img
                    src={flowerImage}
                    alt="flower"
                    className='absolute bottom-[-50px] left-0'
                  />
                  <img
                    src={flowerImage}
                    alt="flower"
                    className='absolute bottom-[-50px] right-0 scale-x-[-1]'
                  />
                </div>

                {/* 聊天訊息區域 */}
                <div
                  className='p-[40px] pt-[60px] h-[70%] flex flex-col gap-4 overflow-y-auto'
                  style={{
                    background: `url(${chatroomBackground}) center/cover`,
                    maxHeight: '70%',
                    minHeight: 0,
                    flexShrink: 0
                  }}
                >
                  {!roomCode ? (
                    <div className='flex items-center justify-center h-full text-center'>
                      <div style={{ color: '#11386F', fontSize: 18 }}>
                        <div>聊天室尚未設定</div>
                        <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>
                          請聯繫管理員設定聊天室
                        </div>
                      </div>
                    </div>
                  ) : !isJoinedChat ? (
                    <div className='flex items-center justify-center h-full text-center'>
                      <div style={{ color: '#11386F', fontSize: 18 }}>
                        <div style={{ marginBottom: 16 }}>歡迎來到線上告別式聊天室</div>
                        <div style={{ fontSize: 14, marginBottom: 24, opacity: 0.7 }}>
                          在這裡與其他親友分享您的思念與祝福
                        </div>
                        <Button
                          type="primary"
                          size="large"
                          onClick={handleJoinChat}
                          style={{
                            background: '#11386F',
                            borderColor: '#11386F',
                            borderRadius: 8,
                            padding: '8px 24px',
                            fontSize: 16,
                            fontWeight: 500
                          }}
                        >
                          加入聊天室
                        </Button>
                      </div>
                    </div>
                  ) : chatMessages.map((msg: ChatMessage) => {
                    const style = getMessageStyle(msg.type);
                    return (
                      <div
                        key={msg.id}
                        style={{
                          background: style.background,
                          borderRadius: 8,
                          padding: '4px 8px',
                        }}
                      >
                        <ul className='flex-1 flex items-center gap-2 text-[20px]'>
                          <li
                            style={{
                              fontWeight: msg.type !== CHAT_MESSAGE_TYPES.MESSAGE ? 500 : 400,
                              color: style.userColor,
                            }}
                            className='self-start'
                          >
                            <span className=''>{msg.user}</span>
                          </li>
                          <li
                            style={{
                              flex: 1,
                              color: style.textColor,
                              lineHeight: 1.2,
                            }}
                          >
                            {msg.message}
                          </li>
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {/* 聊天輸入區域 */}
                {isJoinedChat && roomCode ? (
                  <div className='p-2 w-full h-[20%] flex flex-col justify-center items-center gap-4 bg-[#EDE8DD] shadow-sm'>
                    {/* 快速功能按鈕 */}
                    <div className='w-full flex justify-between gap-2'>
                      {[
                        { key: 'blessing', label: '線上捻香', color: 'rgba(17, 56, 111, 0.56)' },
                        { key: 'memorial', label: '線上悼念', color: 'rgba(17, 56, 111, 0.56)' },
                        { key: 'flower', label: '線上獻花', color: 'rgba(17, 56, 111, 0.56)' }
                      ].map((action) => (
                        <Button
                          key={action.key}
                          onClick={() => {
                            if (!isSending && isConnected) {
                              setSelectedQuickAction(action.key);
                              sendQuickAction(action.key);
                            }
                          }}
                          loading={isSending}
                          disabled={!isConnected || isSending}
                          style={{
                            flex: 1,
                            background: action.color,
                            border: 'none',
                            borderRadius: 30,
                            color: '#FFFFFF',
                            // fontSize: 18,
                            fontWeight: 500,
                            letterSpacing: '5%'
                          }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>

                    {/* 輸入框 */}
                    <div className='w-full flex items-center gap-4 bg-[rgba(255, 255, 255, 0.4)] rounded-lg' >
                      <Input
                        placeholder={isConnected ? "輸入想說的話" : "正在連接聊天室..."}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onPressEnter={() => {
                          if (chatInput.trim() && isConnected && !isSending) {
                            sendMessage(chatInput);
                            setChatInput('');
                          }
                        }}
                        disabled={!isConnected || isSending}
                        style={{
                          padding: '10px 16px',
                          flex: 1,
                          width: '100%',
                          border: 'none',
                          background: '#FFFFFF66',
                          fontSize: 20,
                          color: '#11386F8F',
                        }}
                        allowClear
                        suffix={
                          <Button
                            type="text"
                            icon={<SendIcon />}
                            loading={isSending}
                            disabled={!isConnected || !chatInput.trim() || isSending}
                                                          onClick={() => {
                                if (chatInput.trim() && isConnected && !isSending) {
                                  sendMessage(chatInput);
                                  setChatInput('');
                                }
                              }}
                          />
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className='w-full h-[20%] bg-[#EDE8DD] shadow-sm'></div>
                )}
              </div>
            </Col>
          </Row>
        </div>
        <VideoSection />

        {/* 加入聊天室彈窗 */}
        <Modal
          title="加入聊天室"
          open={showJoinModal}
          onOk={handleConfirmJoin}
          onCancel={handleCancelJoin}
          okText="加入"
          cancelText="取消"
          centered
          width={400}
          okButtonProps={{
            style: {
              background: '#11386F',
              borderColor: '#11386F'
            }
          }}
        >
          <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: 16, color: '#666', fontSize: 14 }}>
              請輸入您的名稱，這將作為您在聊天室中的顯示名稱
            </div>
            <Input
              placeholder="請輸入您的名稱"
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              onPressEnter={handleConfirmJoin}
              maxLength={20}
              style={{
                fontSize: 16,
                padding: '8px 12px'
              }}
              autoFocus
            />
            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
              名稱長度限制為 20 個字符
            </div>
          </div>
        </Modal>
      </div>
    </Spin>
  );
};

export default LiveStream; 